<template>
    <shift-loader
        name='actuals'
        :isLoading='!dataLoaded'
        @loaded='loadData'
    >
        <div style='width: 100%; align-self: baseline;' class='mt-6 pr-15 pl-15'
             :class="{ 'edit-mode': editing, 'read-mode': !editing }">
            <v-tabs
                color='deep-purple accent-4'
            >
                <v-tab key='actualsEntry'>Plod Entry</v-tab>
                <v-tab key='highLevelActuals'>Key Physicals</v-tab>
                <v-tab key='equipmentDowntime'>{{ 'Equipment' | term }} Downtime</v-tab>
                <v-tab key='inspections' v-if="showInspectionTab">Inspections</v-tab>

                <v-chip
                    v-if='!editing'
                    class='ma-2 status-chips'
                    text-color='white'
                    color='secondary'
                >
                    Read Only
                </v-chip>
                <v-chip
                    v-if='editing'
                    class='ma-2 status-chips'
                    text-color='white'
                    color='green'
                >
                    Editing
                </v-chip>

                <v-spacer />

                <div class='align-self-center'>
                    <v-btn
                        v-if="showClearButton"
                        color='error'
                        @click='confirmClearData()'
                        class='mr-4 '>
                        Clear Data
                    </v-btn>

                    <v-btn
                        color='primary'
                        @click='showExportToCsvModal = true'
                        class='ml-2 '>
                        Export to CSV
                    </v-btn>

                    <v-btn
                        v-if='!editing'
                        @click='toggleMode()'
                        color='primary'
                        class='ml-2 '>
                        Enter Edit Mode
                    </v-btn>

                    <v-btn
                        v-if='editing'
                        @click='toggleMode()'
                        color='primary'
                        class='ml-2 '>
                        Save And Exit
                    </v-btn>
                </div>

                <v-tab-item class="actuals-tab-item">
                    <plod-entry
                        ref='childPlodEntry'
                        v-if='dataLoaded'
                        :editing='editing'
                        :actual-cuts="actualCuts"
                        :refresh-required-toggle="actualsRefreshRequiredToggle"
                        @refresh-required="onActualsRefreshRequested"
                    />
                </v-tab-item>

                <v-tab-item class="actuals-tab-item">
                    <key-physical-actuals ref='childKeyPhysicals'
                                          v-if='dataLoaded'
                                          :locations="locations"
                                          :actual-cuts="actualCuts"
                                          :shift-id="shiftId"
                                          :editing="editing"
                                          :refresh-required-toggle="actualsRefreshRequiredToggle"
                                          @refresh-required="onActualsRefreshRequested"
                    >

                    </key-physical-actuals>
                </v-tab-item>

                <v-tab-item class="actuals-tab-item">
                    <equipment-downtime
                        ref='childEquipmentDowntime'
                        v-if='dataLoaded'
                        :shift-id='shiftId'
                        :editing='editing'
                    />
                </v-tab-item>

                <v-tab-item class="actuals-tab-item">
                    <shift-inspections
                        v-if='dataLoaded'
                        :shift-id='shiftId'
                    />
                </v-tab-item>

            </v-tabs>

        </div>
        <standard-dialog :render='true' :value='showExportToCsvModal' :max-width='500' persistent>
            <template v-slot:title>
                Export to CSV
            </template>
            <template v-slot:content>

                <v-form ref='form' :lazy-validation='true'>
                    <v-radio-group
                        v-model='csvExportOption'
                        :rules='[$rules.required()]'
                    >
                        <v-radio label='Export data for current shift (not implemented)' value='currentShift'
                                 class='mb-5'></v-radio>
                        <v-radio label='Export data by date range' value='dateRange'></v-radio>
                    </v-radio-group>

                    <v-row v-if="csvExportOption === 'dateRange'" class='ml-8'>
                        <v-text-field
                            v-model='csvExportDateRangeStart'
                            label='Start Date'
                            type='date'
                            :rules='[$rules.required()]'
                            class='mr-5'
                        ></v-text-field>
                        <v-text-field
                            v-model='csvExportDateRangeEnd'
                            label='End Date'
                            type='date'
                            :rules='[$rules.required()]'
                            class='mr-10'
                        ></v-text-field>
                    </v-row>
                </v-form>

                <div class='ml-8' style='width: 200px;'>

                </div>

            </template>
            <template v-slot:actions>
                <v-spacer />
                <v-btn color='gray' text @click='showExportToCsvModal = false'>Cancel</v-btn>
                <v-btn color='primary' text @click='downloadCSV()'>Download</v-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>
    </shift-loader>
</template>

<script>
import Actuals from '@/lib/data/Actuals';
import { useSystemStore } from '@/lib/stores/SystemStore';
import ShiftLoader from '@/components/ShiftBoard/ShiftLoader.vue';
import { mapState } from 'pinia';
import { useShiftDetails } from '@/lib/stores/Shift';
import { useDepartmentStore } from '@/lib/stores/DepartmentStore';
import { useShiftHighLevelActuals } from '@/lib/stores/Shift/ShiftHighLevelActualsStore';
import { EventBus, Events } from '@/lib/EventBus';
import DownloadHelper from '@/lib/DownloadHelper';
import PlodEntry from '@/components/Actuals/PlodEntry.vue';
import EquipmentDowntime from '@/components/Actuals/EquipmentDowntime.vue';
import KeyPhysicalActuals from '@/components/Actuals/KeyPhysicalActuals.vue';
import ShiftInspections from '@/components/ShiftBoard/Actuals/ShiftInspections.vue';
import Logging from '@/lib/Logging';
import dayjs from 'dayjs';
import SelectedMineAreaStore from '@/lib/stores/SelectedMineAreaStore';
import Config from '@/lib/Config';

export default {
    components: { KeyPhysicalActuals, EquipmentDowntime, PlodEntry, ShiftLoader, ShiftInspections },
    computed: {
        ...mapState(useDepartmentStore, ['departmentId', 'locations', 'locationName', 'taskTypes']),
        ...mapState(useShiftDetails, ['shiftId', 'shiftName', 'shiftStartTime', 'shiftEndTime', 'supervisorShiftReport']),
        ...mapState(useSystemStore, ['staffMembers']),
        ...mapState(useShiftHighLevelActuals, ['shiftPredictions', 'shiftDowntime']),
        isOverAWeekAgo() {
            return this.instantiatedMineTime != null && this.shiftEndTime.isBefore(this.instantiatedMineTime.add(-7, 'day'));
        },
        showClearButton() {
            return this.editing && !this.isOverAWeekAgo;
        },
        showInspectionTab() {
            return Config?.FeatureFlags?.EnableOrdoConnect ?? false;
        }
    },
    data() {
        return {
            heading: 'Actuals Entry',
            editing: true,
            dataLoaded: false,
            showExportToCsvModal: false,
            csvExportOption: null,
            csvExportDateRangeStart: null,
            csvExportDateRangeEnd: null,
            actualsRefreshRequiredToggle: false,
            instantiatedMineTime: null,
            actualCuts: []
        };
    },
    created() {
    },
    mounted() {
    },
    methods: {
        async toggleMode() {
            if (this.editing) {
                let highLevelPhysicalsErrored = false;
                let plodEntryErrored = false;
                let equipmentDowntimeErrored = false;

                let errored = [];

                if (this.$refs.childKeyPhysicals && !(await this.$refs.childKeyPhysicals.save()))
                    errored.push('Key Physicals');

                if (this.$refs.childPlodEntry && !(await this.$refs.childPlodEntry.save()))
                    errored.push('Plod Entry');

                if (this.$refs.childEquipmentDowntime && !(await this.$refs.childEquipmentDowntime.save()))
                    errored.push(`${this.$termSync("equipment")} Downtime`);

                if (errored.length > 0) {
                    EventBus.$emit(Events.ToastError, `Error has occured with saving: ${errored.join(', ')}. Please try again.`);
                } else
                    this.editing = false;
            } else {
                this.editing = true;
            }
        },
        onActualsRefreshRequested() {
            this.loadData();
            this.actualsRefreshRequiredToggle = !this.actualsRefreshRequiredToggle;
        },
        loadData() {
            var mine = SelectedMineAreaStore.getSelectedMineArea();
            // @ts-ignore
            this.instantiatedMineTime = dayjs().tz(mine.timeZone);
            Actuals.getActualCutsIncludingPredictionsForLocations(this.departmentId)
                .then(response => {
                    this.actualCuts = response?.actualCuts || [];
                })
                .catch(error => {
                    Logging.error(error);
                    this.actualCuts = [];
                })
                .finally(() => {
                    this.dataLoaded = true;
                });
        },
        async downloadCSV(filename = 'export.csv') {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (this.csvExportOption === 'currentShift') {
                // TODO: Implement export CSV for current shift
            } else {
                let response = await Actuals.downloadCsv(this.departmentId, this.csvExportDateRangeStart, this.csvExportDateRangeEnd);

                let blob = new Blob([response.data], { type: 'text/csv' });
                DownloadHelper.download(`ActualsReport_${this.csvExportDateRangeStart}_${this.csvExportDateRangeEnd}.csv`, blob);
            }

            this.showExportToCsvModal = false;
        },
        async confirmClearData() {
            if (await this.$refs.confirm.multilineOpen('Clear Data', ['Are you sure you want to clear all actuals data for this shift? This will permanently delete all Plod Entry, Key Physicals, and Equipment Downtime data.', 'This action cannot be undone.'], { color: 'red' })) {
                await this.clearAllData();
            }
        },
        async clearAllData() {
            try {
                await Actuals.clearActualsForShift(this.departmentId, this.shiftId);
                EventBus.$emit(Events.ToastSuccess, `Successfully cleared all data for shift.`);
                this.onActualsRefreshRequested();
            } catch (error) {
                EventBus.$emit(Events.ToastError, `Error while clearing data for shift.`);
                Logging.error(error);
            }
        }
    }
};
</script>

<style scoped>
.status-chips {
    font-weight: bold;
}

>>> .icon-column {
    max-width: 53px;
    width: 53px;
    padding: 0 !important;
}

>>> .small-input {
    font-size: 14px;
    margin-top: 0;
}

.screen-x .actuals-tab-item >>> .small-input, .screen-l .actuals-tab-item >>> .small-input {
    font-size: 10px;

    .v-input__slot {
        min-height: 30px !important;

        .v-input__append-inner {
            margin-top: 4px !important;
        }

        textarea {
            margin-top: 0px;
        }
    }

    .v-select__slot {
        height: 30px !important;

        .v-input__append-inner {
            margin-top: 4px !important;
        }
    }
}

>>> .custom-table {
    td {
        padding: 8px 5px !important;
    }

    th {
        border-bottom: 0 !important;
        text-align: center !important;
    }
}

>>> .icon-column {
    min-width: 50px;
}

>>> .edit-mode .custom-table {
    th {
        background-color: #15A69A;
        color: #FFF !important;
        border: 1px SOLID #FFF;
    }
}

>>> .read-mode .custom-table {
    th {
        background-color: #DDD;
        color: #000 !important;
        border: 1px SOLID #FFF;
    }
}
</style>
